import _ from 'underscore';
import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import Promise from 'bluebird';
import { Button } from '@fluentui/react-components';

import { CouponFlag } from './coupon-flag';
import { ITip } from '../types';
import { logApi, TipDismissalExpirationDays } from '../const';

export const TipItem = ({
  item, i18n, walkme, jsdata, instrumentationConstants, instrumentationLegacy, logSlideDisplay, removeTipLocally,
}: {
  item: ITip;
  i18n: {
    getString: (key: string) => string;
  };
  walkme: any;
  jsdata: any;
  instrumentationConstants: any,
  instrumentationLegacy: any,
  logSlideDisplay: ({ reason }: { reason: string }) => void;
  removeTipLocally: (tipId: string) => void;
}) => {
  const videoRef = useRef(null);
  const clickTip = useCallback(() => {
    const { tipId, helpType, helpLink } = item;

    if (helpType === 'tour') {
      if (window.WalkMeAPI && _.isFunction(window.WalkMeAPI.startWalkthruById)) {
        window.WalkMeAPI.startWalkthruById(helpLink);
      } else {
        walkme.whenLoaded().then(() => {
          window.WalkMeAPI.startWalkthruById(helpLink);
        });
      }
    } else if (helpType === 'video') {
      // Trigger video popup.
      // ApexVideoHandler removes all other event handler before attaching event handler for video popup. It also stops click event bubbling up.
      // So, we are using hidden button instead of on click button on the tip.
      videoRef.current.click();
    }

    instrumentationLegacy.logMessage([instrumentationConstants.LOGCATEGORY.TRACE], instrumentationConstants.LOGLEVEL.INFO, logApi, `Learn more clicked. Tip Id: ${tipId}`);

    jsdata.Tip.update(tipId, {
      TipId: tipId,
      Status: 'Clicked',
    }, {
      afterUpdate: () => Promise.resolve({ TipId: tipId }),
    });
  }, [instrumentationConstants.LOGCATEGORY.TRACE, instrumentationConstants.LOGLEVEL.INFO, instrumentationLegacy, item, jsdata.Tip, walkme]);

  const dismissTip = useCallback(() => {
    const { tipId, helpType, helpLink } = item;

    instrumentationLegacy.logMessage([instrumentationConstants.LOGCATEGORY.TRACE], instrumentationConstants.LOGLEVEL.INFO, logApi, `Dismiss clicked. Tip Id: ${tipId}`);

    removeTipLocally(tipId);

    logSlideDisplay({ reason: 'dismissed current' });

    const expirationTime = new Date();

    expirationTime.setDate(expirationTime.getDate() + TipDismissalExpirationDays);

    jsdata.Tip.update(tipId, {
      TipId: tipId,
      Status: 'Dismissed',
      DismissalExpirationDateTime: expirationTime,
    }, {
      afterUpdate: () => Promise.resolve({ TipId: tipId }),
    });
  }, [instrumentationConstants, instrumentationLegacy, item, jsdata.Tip, logSlideDisplay, removeTipLocally]);

  const couponFlag = useMemo(() => {
    if (item.coupon) {
      return <CouponFlag coupon={item.coupon} i18n={i18n} rightAlign />;
    }
    return null;
  }, [i18n, item]);

  return (
    <>
      <div className="carousel__tip_content">
        {couponFlag}
        <div className="tip_image_container">
          <div className={classNames('tip_image', item.imgClass)} />
        </div>
        <div className="tip_text_content">
          <div className="tip_title">{item.title}</div>
          <div className="tip_description__wrapper">
            <p className="tip_description">{item.description}</p>
          </div>
        </div>
      </div>
      <div className="tip_links">
        {item.helpType === 'side_pane' && (
          <span className="tip_clickLink" role="presentation" tabIndex={-1} onClick={clickTip}>
            {/* @ts-ignore */}
            <button className="btn-default btn HelpTopic" query={`help:${item.helpLink}`} type="button">
              {i18n.getString(_TL_('Learn more'))}
            </button>
          </span>
        )}
        {item.helpType === 'new_page' && (
          <span className="tip_clickLink" role="presentation" tabIndex={-1} onClick={clickTip}>
            <a className="linkButton btn-default btn" href={item.helpLink} target="_blank" rel="noreferrer">
              {i18n.getString(_TL_('Learn more'))}
            </a>
          </span>
        )}
        {item.helpType === 'video' && (
          <span className="tip_clickLink" role="presentation" tabIndex={-1}>
            <Button onClick={clickTip}>
              {i18n.getString(_TL_('Watch now'))}
            </Button>
            {/* @ts-ignore */}
            <div className="abLaunchVideo" ref={videoRef} videoId={item.helpLink} style={{ display: 'none' }} />
          </span>
        )}
        {item.helpType === 'tour' && (
          <span className="tip_clickLink" role="presentation" tabIndex={-1}>
            <Button onClick={clickTip}>
              {i18n.getString(_TL_('Guide me'))}
            </Button>
          </span>
        )}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className="tip_dismissLink clickable"
          tabIndex={0}
          role="button"
          aria-label={i18n.getString(_TL_('Dismiss'))}
          onClick={dismissTip}
          onKeyUp={dismissTip}
        >
          {i18n.getString(_TL_('Dismiss'))}
        </a>
      </div>
    </>
  );
};
